import { flatten, hasValue, mergeDefinition, validateEffective, validateTemplate, type Condition, type DefinitionError, type FieldDef, type FormDefinition, type JsonValue, type Operator } from '../../../form-engine';

export interface Located {
  field: FieldDef;
  parent: FieldDef | null;
  index: number;
  siblings: FieldDef[];
}

export function locate(fields: FieldDef[], key: string, parent: FieldDef | null = null): Located | null {
  for (let i = 0; i < fields.length; i++) {
    const f = fields[i]!;
    if (f.key === key) return { field: f, parent, index: i, siblings: fields };
    const inner = locate(f.children ?? [], key, f);
    if (inner) return inner;
  }
  return null;
}

function mapFields(fields: FieldDef[], fn: (list: FieldDef[], parent: FieldDef | null) => FieldDef[], parent: FieldDef | null = null): FieldDef[] {
  const mapped = fn(fields, parent).map((f) => (f.children ? { ...f, children: mapFields(f.children, fn, f) } : f));
  return mapped;
}

/** Replaces the field with `key` (keeps its position). */
export function replaceField(def: FormDefinition, key: string, next: FieldDef): FormDefinition {
  return { fields: mapFields(def.fields, (list) => list.map((f) => (f.key === key ? { ...next, children: next.children ?? f.children } : f))) };
}

/** Adds a field to the top level or to the children of `parentKey`: at `index`, or at the end without one. */
export function addField(def: FormDefinition, field: FieldDef, parentKey: string | null, index?: number | null): FormDefinition {
  const insert = (list: FieldDef[]) => {
    const at = index === undefined || index === null ? list.length : Math.min(Math.max(index, 0), list.length);
    return [...list.slice(0, at), field, ...list.slice(at)];
  };
  if (!parentKey) return { fields: insert(def.fields) };
  return { fields: mapFields(def.fields, (list) => list.map((f) => (f.key === parentKey ? { ...f, children: insert(f.children ?? []) } : f))) };
}

/**
 * Adds a top-level custom field right after its anchor (`placeAfter`), i.e. before the custom fields already anchored
 * there (the same anchor keeps the array order).
 */
export function addCustomField(custom: FieldDef[], field: FieldDef): FieldDef[] {
  const anchor = field.placeAfter ?? null;
  const at = custom.findIndex((c) => (c.placeAfter ?? null) === anchor);
  return at < 0 ? [...custom, field] : [...custom.slice(0, at), field, ...custom.slice(at)];
}

export function removeField(def: FormDefinition, key: string): FormDefinition {
  return { fields: mapFields(def.fields, (list) => list.filter((f) => f.key !== key)) };
}

/** Moves a field one position up (-1) or down (+1) among its siblings; null at the boundary. */
export function moveField(def: FormDefinition, key: string, direction: -1 | 1): FormDefinition | null {
  const loc = locate(def.fields, key);
  if (!loc) return null;
  const target = loc.index + direction;
  if (target < 0 || target >= loc.siblings.length) return null;
  const reorder = (list: FieldDef[]) => {
    if (list !== loc.siblings) return list;
    const next = [...list];
    [next[loc.index], next[target]] = [next[target]!, next[loc.index]!];
    return next;
  };
  if (!loc.parent) return { fields: reorder(def.fields) };
  return { fields: mapFields(def.fields, (list) => reorder(list)) };
}

/** Sections a field can be moved into (not a section itself; not out of or into repeating groups). */
export function canMoveToSection(def: FormDefinition, key: string): boolean {
  const loc = locate(def.fields, key);
  if (!loc || loc.field.type === 'SECTION') return false;
  return loc.parent === null || loc.parent.type === 'SECTION';
}

/**
 * Template: moves a field into a section (appended at its end) or, with `sectionKey` null, out of its section to
 * the top level right after the section. Null when nothing changes.
 */
export function moveToSection(def: FormDefinition, key: string, sectionKey: string | null): FormDefinition | null {
  const loc = locate(def.fields, key);
  if (!loc || !canMoveToSection(def, key) || (loc.parent?.key ?? null) === sectionKey) return null;
  const without = removeField(def, key);
  if (sectionKey) return addField(without, loc.field, sectionKey);
  const at = without.fields.findIndex((f) => f.key === loc.parent!.key);
  const fields = [...without.fields];
  fields.splice(at + 1, 0, loc.field);
  return { fields };
}

/**
 * Custom fields: moves a custom field into one of the team's sections (it loses its anchor) or, with `sectionKey`
 * null, out of it to the top level right after the section. Fields anchored to the moved field take over its anchor.
 */
export function moveCustomToSection(custom: FieldDef[], key: string, sectionKey: string | null): FieldDef[] | null {
  const loc = locate(custom, key);
  if (!loc || loc.field.type === 'SECTION' || (loc.parent?.key ?? null) === sectionKey) return null;
  if (loc.parent && loc.parent.type !== 'SECTION') return null;
  const moved: FieldDef = { ...loc.field };
  const oldAnchor = loc.parent ? null : (loc.field.placeAfter ?? null);
  let list = removeField({ fields: custom }, key).fields.map((c) => (c.placeAfter === key ? { ...c, placeAfter: oldAnchor } : c));
  if (sectionKey) {
    delete moved.placeAfter;
    return addField({ fields: list }, moved, sectionKey).fields;
  }
  moved.placeAfter = loc.parent!.key;
  const at = list.findIndex((c) => c.key === loc.parent!.key);
  list = [...list];
  list.splice(at + 1, 0, moved);
  return list;
}

/**
 * Template: moves a field (with its fields, for a group) to `index` of the top level or of the fields of `parentKey`;
 * `index` counts the places before the move. Null when it stays where it is.
 */
export function moveFieldTo(def: FormDefinition, key: string, parentKey: string | null, index: number): FormDefinition | null {
  const loc = locate(def.fields, key);
  if (!loc) return null;
  const same = (loc.parent?.key ?? null) === parentKey;
  if (same && (index === loc.index || index === loc.index + 1)) return null;
  return addField(removeField(def, key), loc.field, parentKey, same && index > loc.index ? index - 1 : index);
}

/**
 * Custom fields: moves a custom field to `index` of the effective top level (the template's and the team's fields;
 * every top-level custom field is then anchored after its predecessor) or of the fields of one of the team's groups
 * (it has no anchor there; fields anchored to it take over its anchor). Null when it stays where it is.
 */
export function moveCustomFieldTo(template: FormDefinition, custom: FieldDef[], key: string, parentKey: string | null, index: number): FieldDef[] | null {
  const loc = locate(custom, key);
  if (!loc) return null;
  if (parentKey) {
    const moved = moveFieldTo({ fields: custom }, key, parentKey, index);
    if (!moved || loc.parent) return moved?.fields ?? null;
    // from the top level into a group: no anchor any more
    const oldAnchor = loc.field.placeAfter ?? null;
    const reanchor = (list: FieldDef[]): FieldDef[] =>
      list.map((c) => {
        if (c.key === key) {
          const rest = { ...c };
          delete rest.placeAfter;
          return rest;
        }
        const next = c.placeAfter === key ? { ...c, placeAfter: oldAnchor } : c;
        return next.children ? { ...next, children: reanchor(next.children) } : next;
      });
    return reanchor(moved.fields);
  }
  const order = [...mergeDefinition(template, custom).fields];
  if (loc.parent) {
    // out of a group to the top level
    const without = removeField({ fields: custom }, key).fields;
    const top = [...mergeDefinition(template, without).fields];
    top.splice(Math.min(index, top.length), 0, loc.field);
    return anchorCustomFields(top);
  }
  const at = order.findIndex((f) => f.key === key);
  if (index === at || index === at + 1) return null;
  const [field] = order.splice(at, 1);
  order.splice(index > at ? index - 1 : index, 0, field!);
  return anchorCustomFields(order);
}

const ORDER_CODES = new Set(['ref.forward', 'ref.scope']);

/** Errors the change introduces that concern the order of fields (a rule referring to a later field). */
export function newOrderErrors(before: DefinitionError[], after: DefinitionError[]): DefinitionError[] {
  const known = new Set(before.map((e) => `${e.field}|${e.code}|${e.rule ?? ''}`));
  return after.filter((e) => ORDER_CODES.has(e.code) && !known.has(`${e.field}|${e.code}|${e.rule ?? ''}`));
}

export function newErrors(before: DefinitionError[], after: DefinitionError[]): DefinitionError[] {
  const known = new Set(before.map((e) => `${e.field}|${e.code}|${e.rule ?? ''}`));
  return after.filter((e) => !known.has(`${e.field}|${e.code}|${e.rule ?? ''}`));
}

/** Keys referenced by a condition. */
export function references(c: Condition | undefined): string[] {
  if (!c) return [];
  if ('all' in c) return c.all.flatMap(references);
  if ('any' in c) return c.any.flatMap(references);
  if ('not' in c) return references(c.not);
  return [c.field.startsWith('row.') ? c.field.slice(4) : c.field];
}

/** Fields whose rules reference `key`. */
export function dependentsOf(def: FormDefinition, key: string): FieldDef[] {
  return flatten(def.fields).filter((f) => [f.visibleWhen, f.enabledWhen, f.requiredWhen].some((c) => references(c).includes(key)));
}

// ------------------------------------------------------------------------------- custom fields (top level)

/** Re-anchors every custom field after its predecessor in the given top-level order. */
export function anchorCustomFields(order: FieldDef[]): FieldDef[] {
  const custom: FieldDef[] = [];
  order.forEach((f, i) => {
    if (f.origin === 'INSPECTION') custom.push({ ...f, placeAfter: i === 0 ? null : order[i - 1]!.key });
  });
  return custom;
}

/** Moves a custom field one position in the effective top-level order. */
export function moveCustomField(template: FormDefinition, custom: FieldDef[], key: string, direction: -1 | 1): FieldDef[] | null {
  const order = [...mergeDefinition(template, custom).fields];
  const i = order.findIndex((f) => f.key === key);
  const j = i + direction;
  if (i < 0 || j < 0 || j >= order.length) return null;
  [order[i], order[j]] = [order[j]!, order[i]!];
  return anchorCustomFields(order);
}

export function validateFor(mode: 'template' | 'custom', def: FormDefinition, template: FormDefinition, custom: FieldDef[]): DefinitionError[] {
  return mode === 'template' ? validateTemplate(def) : validateEffective(template, custom);
}

// ------------------------------------------------------------------------------- keys and option codes

function ascii(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/** "Termín splnenia opatrení" → "terminSplneniaOpatreni" (unique among `taken`). */
export function suggestKey(label: string, taken: Set<string>): string {
  const words = ascii(label).toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  let key = words.map((w, i) => (i === 0 ? w : w.charAt(0).toUpperCase() + w.slice(1))).join('') || 'pole';
  if (!/^[a-z]/.test(key)) key = `pole${key.charAt(0).toUpperCase()}${key.slice(1)}`;
  key = key.slice(0, 36);
  let candidate = key;
  for (let n = 2; taken.has(candidate); n++) candidate = `${key}${n}`;
  return candidate;
}

/** "málo vyhovujúci" → "MALO_VYHOVUJUCI". */
export function suggestOptionValue(label: string, taken: Set<string>): string {
  let value = ascii(label).toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 36) || 'MOZNOST';
  const base = value;
  for (let n = 2; taken.has(value); n++) value = `${base}_${n}`;
  return value;
}

export const KEY_PATTERN = /^[a-z][a-zA-Z0-9]{0,39}$/;

/** The entry of a TABLE column / row header in the set of locked options ("column:C1"; option values have no colon). */
export function tableLock(kind: 'column' | 'row', key: string): string {
  return `${kind}:${key}`;
}

// ------------------------------------------------------------------------------- rule editor model

export interface EditorLeaf {
  kind: 'leaf';
  id: string;
  field: string;
  op: Operator | '';
  value?: JsonValue;
  /** compared with the value of this field instead of a fixed value ('' while none is chosen yet) */
  valueField?: string;
}

export interface EditorGroup {
  kind: 'group';
  id: string;
  combinator: 'all' | 'any';
  items: EditorLeaf[];
}

export interface EditorRule {
  combinator: 'all' | 'any';
  items: (EditorLeaf | EditorGroup)[];
}

let counter = 0;
export const newId = () => `c${++counter}`;

export function emptyLeaf(): EditorLeaf {
  return { kind: 'leaf', id: newId(), field: '', op: '' };
}

/** AST → editor model; 'unsupported' for rules the structured editor cannot show (e.g. `not`, deeper nesting). */
export function toEditor(c: Condition | undefined): EditorRule | null | 'unsupported' {
  if (!c) return null;
  const leaf = (x: Condition): EditorLeaf | null => {
    if (!('field' in x)) return null;
    return x.valueField !== undefined ? { kind: 'leaf', id: newId(), field: x.field, op: x.op, valueField: x.valueField } : { kind: 'leaf', id: newId(), field: x.field, op: x.op, value: x.value };
  };
  if ('field' in c) return { combinator: 'all', items: [leaf(c)!] };
  if ('not' in c) return 'unsupported';
  const combinator = 'all' in c ? 'all' : 'any';
  const list = 'all' in c ? c.all : c.any;
  const items: (EditorLeaf | EditorGroup)[] = [];
  for (const x of list) {
    if ('field' in x) items.push(leaf(x)!);
    else if ('not' in x) return 'unsupported';
    else {
      const inner = 'all' in x ? x.all : x.any;
      if (!inner.every((y) => 'field' in y)) return 'unsupported';
      items.push({ kind: 'group', id: newId(), combinator: 'all' in x ? 'all' : 'any', items: inner.map((y) => leaf(y)!) });
    }
  }
  return { combinator, items };
}

function leafToAst(l: EditorLeaf): Condition {
  const op = l.op as Operator;
  if (op === 'empty' || op === 'notEmpty') return { field: l.field, op };
  return l.valueField !== undefined ? { field: l.field, op, valueField: l.valueField } : { field: l.field, op, value: l.value ?? null };
}

/** Editor model → AST (a single condition is stored as a leaf). */
export function fromEditor(rule: EditorRule): Condition {
  const items = rule.items.map((x) => (x.kind === 'leaf' ? leafToAst(x) : x.items.length === 1 ? leafToAst(x.items[0]!) : { [x.combinator]: x.items.map(leafToAst) } as Condition));
  if (items.length === 1) return items[0]!;
  return { [rule.combinator]: items } as Condition;
}

/** Fields a rule of `fieldKey` may reference: earlier fields with a value, plus earlier fields of the same row. */
export function referenceCandidates(def: FormDefinition, fieldKey: string | null, parentKey: string | null): { ref: string; field: FieldDef; sameRow: boolean }[] {
  const all = flatten(def.fields);
  const position = fieldKey ? all.findIndex((f) => f.key === fieldKey) : -1;
  const groupKeys = new Set<string>();
  for (const f of all) if (f.type === 'REPEATING_GROUP') (f.children ?? []).forEach((c) => groupKeys.add(c.key));
  const parent = parentKey ? locate(def.fields, parentKey)?.field : fieldKey ? locate(def.fields, fieldKey)?.parent : null;
  const inGroup = parent?.type === 'REPEATING_GROUP' ? parent : null;
  // the end of the field's own list when it is new
  let limit = position;
  if (limit < 0) {
    if (inGroup) {
      const lastChild = inGroup.children?.[inGroup.children.length - 1];
      limit = lastChild ? all.findIndex((f) => f.key === lastChild.key) + 1 : all.findIndex((f) => f.key === inGroup.key) + 1;
    } else if (parent) {
      const lastChild = parent.children?.[parent.children.length - 1];
      limit = lastChild ? all.findIndex((f) => f.key === lastChild.key) + 1 : all.findIndex((f) => f.key === parent.key) + 1;
    } else limit = all.length;
  }
  const result: { ref: string; field: FieldDef; sameRow: boolean }[] = [];
  all.forEach((f, i) => {
    if (i >= limit || !hasValue(f.type)) return;
    if (groupKeys.has(f.key)) {
      if (inGroup && (inGroup.children ?? []).some((c) => c.key === f.key)) result.push({ ref: `row.${f.key}`, field: f, sameRow: true });
      return;
    }
    if (inGroup && f.key === inGroup.key) return;
    result.push({ ref: f.key, field: f, sameRow: false });
  });
  return result;
}
